import { Button } from '@techem/ui-components';
import visualImage from 'presentation/assets/images/visual_price.png';
import { SIZE } from 'baseui/button';
import FormControl from 'presentation/components/FormControl';
import { getPixelSize, ICONSIZE } from 'presentation/components/Icon';
import Input from 'presentation/components/Input';
import { WizardStepsContainer } from 'presentation/components/WizardSteps';
import { API_BASE_URL, DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  setEMailAddress as setEMailAddressInStore,
  setMarketingConsent
} from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { paths } from 'frameworks_drivers/routes';
import store from 'store';
import { FormValidator } from 'tools/FormValidator';
import useAutoFocus from 'tools/hooks/useAutoFocus';
import { useMediaQuery } from 'tools/hooks/useMediaQuery';
import { TrackerUtil } from 'tracking/TrackerUtil';
import { CheckboxInput, PrevButton } from '../../../components/input-email-address-page';

import { DataProtectionClickableLink } from './DataProtectionClickableLink';
import { DataProtectionModal } from './DataProtectionModal';
import { MarketoConsentClient } from '../../../../interface_adapters/clients/MarketoConsentClient';

interface FormData {
  emailAddress: string;
  marketingConsent: boolean;
}

const InputEmailAddressPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const emailInputRef = useAutoFocus();
  const descriptionRef = useRef<any>(null);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      emailAddress: store.getState().onboardingInfo.contactDetails.email,
      marketingConsent: store.getState().onboardingInfo.marketingConsent.consentGiven
    }
  });

  const prevPath = paths.HeatingSources;
  const isAboveLarge = useMediaQuery('(min-width: 992px)');
  const [isDataProtectionModalOpen, setIsDataProtectionModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      // Assumption: previous page has to be filled with user input before the user is allowed to visit this page
      store.getState().onboardingInfo.heatingSources.length === 0
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const onSubmit = handleSubmit(({ emailAddress, marketingConsent }, e) => {
    // Submit Marketo Consent
    new MarketoConsentClient(API_BASE_URL).submitConsent(emailAddress, {
      consentGiven: marketingConsent,
      consentVersion: t('pageContactDetailsDataProtectionVersion')
    });

    // Store data into redux
    store.dispatch(setEMailAddressInStore(emailAddress));
    store.dispatch(
      setMarketingConsent({
        consentGiven: marketingConsent,
        consentVersion: t('pageContactDetailsDataProtectionVersion')
      })
    );

    TrackerUtil.trackNextBtnClick();
    navigate(paths.LoadingOffer);

    e?.preventDefault();
  });

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  const buildEMailErrorMessage = () => {
    switch (errors?.emailAddress?.type) {
      case 'required':
        return t('pageContactDetailsErrorEMailAddressEmpty');
      case 'asciiOnly':
        return t('pageContactDetailsErrorEMailAddressNonAscii');
      case 'format':
        return t('pageContactDetailsErrorEMailAddressInvalid');
      default:
        return null;
    }
  };

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer />

      <form onSubmit={onSubmit}>
        <Row className="py-0 justify-content-center">
          <Col xs={12} lg={6}>
            <Row>
              <Col className="pt-1 pt-md-4" xs="auto">
                <PrevButton handleClick={handlePrevBtnClicked} />
              </Col>
              <Col xs>
                <div className="py-0 py-md-3">
                  <h1 className="visually-hidden">
                    <Trans i18nKey={'pageGetEMailAddressTitle'} />
                  </h1>
                  <h3 className={`${isAboveLarge ? 'text-left' : 'text-center'} m-auto text-break`}>
                    <Trans i18nKey={'pageGetEMailAddressTitle'} />
                  </h3>
                </div>
              </Col>
              <Col className="pt-1 pt-md-4" xs="auto">
                <div
                  style={{
                    width: getPixelSize(ICONSIZE.medium),
                    height: getPixelSize(ICONSIZE.medium)
                  }}
                ></div>
              </Col>
            </Row>
            <Row className={`${isAboveLarge ? '' : 'justify-content-center'}`}>
              <Col xs="auto" className="d-none d-lg-block">
                <div
                  style={{
                    width: getPixelSize(ICONSIZE.medium),
                    height: getPixelSize(ICONSIZE.medium)
                  }}
                ></div>
              </Col>
              <Col xs={10} md={9}>
                <p className={`copy ${isAboveLarge ? '' : 'text-center'}`}>
                  {t('pageGetEMailAddressDescription')}
                </p>
              </Col>
            </Row>
            <Row className={`${isAboveLarge ? '' : 'justify-content-center'}`}>
              <Col xs="auto" className="d-none d-lg-block">
                <div
                  style={{
                    width: getPixelSize(ICONSIZE.medium),
                    height: getPixelSize(ICONSIZE.medium)
                  }}
                ></div>
              </Col>
              <Col xs={10} sm={8} md={6} lg={8} className="mb-4 mt-lg-4">
                <FormControl
                  required
                  label={
                    <span id="email-label">{t('pageGetEMailAddressEMailInputFieldLabel')}</span>
                  }
                  error={buildEMailErrorMessage()}
                  htmlFor="field-email"
                >
                  <Controller
                    name="emailAddress"
                    control={control}
                    rules={{
                      required: true,
                      validate: {
                        asciiOnly: (v) => FormValidator.containsAsciiCharsOnly(v),
                        format: (v) => FormValidator.isValidMailAddress(v)
                      }
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Input
                        id="field-email"
                        aria-labelledby="email-label"
                        aria-required="true"
                        aria-invalid={!!errors.emailAddress}
                        aria-describedby={errors.emailAddress ? 'email-error' : undefined}
                        size={SIZE.default}
                        type="email"
                        error={!!errors.emailAddress}
                        value={value}
                        onChange={onChange}
                        autoComplete="email"
                        additionalTestId="emailAddress"
                        inputRef={emailInputRef}
                      />
                    )}
                  />
                </FormControl>
              </Col>
            </Row>
          </Col>
          <Col lg={6} className="d-none d-lg-block">
            <img className="mw-100 h-auto" src={visualImage} alt="angebot" />
          </Col>
        </Row>

        <Row className="justify-content-center py-0 pt-lg-4">
          <Col xs="auto" className="d-none d-sm-block">
            <div
              style={{
                width: getPixelSize(ICONSIZE.medium),
                height: getPixelSize(ICONSIZE.medium)
              }}
            ></div>
          </Col>
          <Col xs={11} sm>
            <Controller
              control={control}
              rules={{
                required: false
              }}
              render={({ field: { onChange, value } }) => (
                <CheckboxInput
                  onChange={(e) => {
                    TrackerUtil.trackCheckboxClick(
                      `${TrackerUtil.getPageName()}_checkbox_marketing-consent`,
                      'checkbox_marketing-consent'
                    );
                    onChange(e);
                  }}
                  value={value}
                />
              )}
              name="marketingConsent"
            />
          </Col>
          <Col xs="auto" className="d-none d-sm-block">
            <div
              style={{
                width: getPixelSize(ICONSIZE.medium),
                height: getPixelSize(ICONSIZE.medium)
              }}
            ></div>
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col xs={'auto'} className="text-center">
            <Button type="submit" size={SIZE.large} additionalTestId="next">
              {t('pageContactDetailsNextButtonText')}
            </Button>
          </Col>
        </Row>

        <Row className="justify-content-center mb-5">
          <Col xs="auto" className="d-none d-sm-block">
            <div
              style={{
                width: getPixelSize(ICONSIZE.medium),
                height: getPixelSize(ICONSIZE.medium)
              }}
            ></div>
          </Col>
          <Col xs={11} sm className="text-center" ref={descriptionRef}>
            <Trans
              i18nKey="pageContactDetailsDataProtectionDescription"
              tOptions={{ skipPostProcessing: true }}
              values={{
                continueButtonText: t('pageContactDetailsNextButtonText')
              }}
            >
              <DataProtectionClickableLink
                handleOpenModal={setIsDataProtectionModalOpen}
                text={t('pageContactDetailsDataProtectionLabel')}
              />
            </Trans>
          </Col>
          <Col xs="auto" className="d-none d-sm-block">
            <div
              style={{
                width: getPixelSize(ICONSIZE.medium),
                height: getPixelSize(ICONSIZE.medium)
              }}
            ></div>
          </Col>
        </Row>
      </form>

      <DataProtectionModal
        isOpen={isDataProtectionModalOpen}
        onClose={() => setIsDataProtectionModalOpen(false)}
      />
    </Container>
  );
};

export default InputEmailAddressPage;
